import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, Search, FileSearch, Globe2, ShieldAlert, UserCheck, Cctv, FileText, Download, Briefcase } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PageShell } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { SeoHead } from "@/components/SeoHead";
import servicesHero from "@/assets/services-hero.jpg";
import servicesHeroVideo from "@/assets/services-hero.mp4.asset.json";
import riskReputationPdf from "@/assets/Comm42-Risk-Reputation-Intelligence-EN.pdf.asset.json";
import { useEffect, useRef, useState } from "react";

export const Route = createFileRoute("/services")({
  head: () => ({
    meta: [
      { title: "Services — Comm42 SA" },
      { name: "description", content: "360° protection: IT investigations, digital forensics, reputation defense, cyber security, personal protection and advanced technology." },
      { property: "og:title", content: "Services — Comm42 SA" },
      { property: "og:description", content: "360° protection, from digital to the road." },
      { property: "og:url", content: "https://comm42.ch/services" },
      { property: "og:type", content: "website" },
    ],
    links: [
      { rel: "canonical", href: "https://comm42.ch/services" },
    ],
  }),
  component: Page,
});

function Page() {
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const isItalian = i18n.language?.toLowerCase().startsWith("it");
  const pdfEyebrow = isItalian ? "Brochure" : "Brochure";
  const pdfTitle = "Risk & Reputation Intelligence";
  const pdfDesc = isItalian
    ? "Scarica la brochure dedicata ai servizi di Risk & Reputation Intelligence (PDF)."
    : "Download our Risk & Reputation Intelligence brochure (PDF).";
  const pdfCta = isItalian ? "Scarica PDF" : "Download PDF";
  const corpEyebrow = "CORPORATE INVESTIGATION";
  const corpTitle = isItalian ? "Corporate Investigation" : "Corporate Investigation";
  const corpDesc = isItalian
    ? "Indagini aziendali riservate, due diligence, asset tracing e analisi del rischio reputazionale per aziende, manager e investitori."
    : "Confidential corporate investigations, due diligence, asset tracing and reputational risk analysis for companies, executives and investors.";
  const corpCta = isItalian
    ? "Scopri i servizi di Corporate Investigation"
    : "Explore our Corporate Investigation services";
  const services = [
    { Icon: Search, title: t("services.items.it.title"), desc: t("services.items.it.desc") },
    { Icon: FileSearch, title: t("services.items.forensics.title"), desc: t("services.items.forensics.desc") },
    { Icon: Globe2, title: t("services.items.reputation.title"), desc: t("services.items.reputation.desc") },
    { Icon: ShieldAlert, title: t("services.items.cyber.title"), desc: t("services.items.cyber.desc") },
    { Icon: UserCheck, title: t("services.items.personal.title"), desc: t("services.items.personal.desc") },
    { Icon: Cctv, title: t("services.items.tech.title"), desc: t("services.items.tech.desc") },
  ];
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const isMobile = window.matchMedia("(max-width: 768px)").matches;
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const saveData = (navigator as any).connection?.saveData === true;
    if (isMobile || reduceMotion || saveData) return;
    setShowVideo(true);
    const v = videoRef.current;
    if (v) {
      v.play().catch(() => setShowVideo(false));
    }
  }, []);

  return (
    <PageShell>
      <SeoHead page="services" />
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border/40 bg-card pt-40 pb-24">
        <img
          src={servicesHero}
          alt="Comm42 global security operations center"
          width={1920}
          height={1080}
          className="absolute inset-0 h-full w-full object-cover opacity-40"
        />
        {showVideo && (
          <video
            ref={videoRef}
            src={servicesHeroVideo.url}
            poster={servicesHero}
            autoPlay
            muted
            loop
            playsInline
            preload="none"
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover opacity-50"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-card/70 via-card/60 to-card" />
        <div className="absolute inset-0 opacity-40 [background-image:radial-gradient(circle_at_30%_20%,oklch(0.6_0.15_240/0.5),transparent_50%),radial-gradient(circle_at_80%_80%,oklch(0.85_0.12_85/0.25),transparent_50%)]" />
        <div className="relative mx-auto max-w-5xl px-6 text-center">
          <p className="reveal-up text-xs tracking-[0.4em] text-primary">{t("services.eyebrow")}</p>
          <h1 className="reveal-up mt-6 font-serif text-5xl md:text-6xl" style={{ animationDelay: "0.15s" }}>
            {t("services.title")}
          </h1>
          <p className="reveal-up mx-auto mt-6 max-w-2xl text-lg text-muted-foreground" style={{ animationDelay: "0.3s" }}>
            {t("services.subtitle")}
          </p>
          <div className="reveal-up mt-10 flex flex-wrap items-center justify-center gap-4" style={{ animationDelay: "0.45s" }}>
            <Link
              to="/contact"
              className="group inline-flex items-center gap-2 bg-foreground px-10 py-3 text-xs tracking-[0.3em] text-background transition-all hover:bg-primary hover:text-primary-foreground hover:shadow-[0_20px_40px_-10px_oklch(0.5_0.09_235/0.4)]"
            >
              {t("services.getStarted")} <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/contact"
              className="group inline-flex items-center gap-2 border border-primary px-10 py-3 text-xs tracking-[0.3em] text-primary transition-all hover:bg-primary hover:text-primary-foreground"
            >
              {t("services.contact")} <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* Services grid */}
      <section className="bg-card/30 py-24">
        <h2 className="sr-only">{t("services.title")}</h2>
        <div className="mx-auto grid max-w-7xl gap-6 px-6 sm:grid-cols-2 lg:grid-cols-3">
          {services.map(({ Icon, title, desc }, i) => (
            <Reveal key={title} delay={i * 80}>
              <div className="tilt-card group relative h-full overflow-hidden border border-border/40 bg-card/60 p-10 transition-colors hover:border-primary/60">
                <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-primary/10 opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100" />
                <div className="relative inline-flex h-12 w-12 items-center justify-center border border-primary/30 bg-primary/10 transition-all duration-500 group-hover:rotate-6 group-hover:border-primary">
                  <Icon className="h-6 w-6 text-primary" />
                </div>
                <h3 className="relative mt-6 font-serif text-2xl">{title}</h3>
                <p className="relative mt-3 text-sm text-muted-foreground">{desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Risk & Reputation Intelligence PDF */}
      <section className="bg-background py-16">
        <div className="mx-auto mb-8 max-w-6xl px-6">
          <Reveal>
            <Link
              to="/services/corporate-investigation"
              className="group relative flex flex-col items-start gap-6 overflow-hidden border border-border/40 bg-card/60 p-10 transition-all hover:border-primary/60 hover:shadow-[0_20px_40px_-10px_oklch(0.5_0.09_235/0.3)] md:flex-row md:items-center md:justify-between"
            >
              <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-primary/10 opacity-0 blur-2xl transition-opacity duration-500 group-hover:opacity-100" />
              <div className="relative flex items-start gap-5">
                <div className="inline-flex h-12 w-12 shrink-0 items-center justify-center border border-primary/30 bg-primary/10 transition-all duration-500 group-hover:rotate-6 group-hover:border-primary">
                  <Briefcase className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-xs tracking-[0.4em] text-primary">{corpEyebrow}</p>
                  <h3 className="mt-3 font-serif text-2xl">{corpTitle}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{corpDesc}</p>
                </div>
              </div>
              <span className="relative inline-flex items-center gap-2 text-xs tracking-[0.3em] text-primary">
                {corpCta} <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          </Reveal>
        </div>
        <div className="mx-auto max-w-6xl px-6">
          <Reveal>
            <div className="group relative flex flex-col items-start gap-6 overflow-hidden border border-border/40 bg-card/60 p-10 transition-colors hover:border-primary/60 md:flex-row md:items-center md:justify-between">
              <div className="flex items-start gap-5">
                <div className="inline-flex h-12 w-12 shrink-0 items-center justify-center border border-primary/30 bg-primary/10">
                  <FileText className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <p className="text-xs tracking-[0.4em] text-primary">{pdfEyebrow}</p>
                  <h3 className="mt-3 font-serif text-2xl">{pdfTitle}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{pdfDesc}</p>
                </div>
              </div>
              <a
                href={riskReputationPdf.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group/btn inline-flex items-center gap-2 bg-foreground px-8 py-3 text-xs tracking-[0.3em] text-background transition-all hover:bg-primary hover:text-primary-foreground"
              >
                <Download className="h-3.5 w-3.5" /> {pdfCta}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden border-y border-border/40 bg-card py-24">
        <div className="absolute inset-0 opacity-20 [background-image:radial-gradient(circle_at_50%_50%,oklch(0.85_0.12_85),transparent_70%)]" />
        <Reveal className="relative mx-auto max-w-3xl px-6 text-center">
          <h2 className="font-serif text-4xl md:text-5xl">{t("services.ctaTitle")}</h2>
          <p className="mt-6 text-muted-foreground">{t("services.ctaDesc")}</p>
          <Link
            to="/contact"
            className="group mt-10 inline-flex items-center gap-2 bg-primary px-10 py-4 text-xs tracking-[0.3em] text-primary-foreground transition-all hover:scale-105 hover:shadow-[0_30px_60px_-15px_oklch(0.5_0.09_235/0.6)]"
          >
            {t("services.contact")} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </section>
    </PageShell>
  );
}
